// Sub-components for ConfigTab - extracted to reduce complexity

import { User, Building2, Phone, Mail, Camera, Loader2 } from 'lucide-react';

// Profile Avatar Section
export function ProfileAvatar({ 
  avatarUrl, 
  isUploadingPhoto, 
  backendAvailable, 
  photoInputRef, 
  onPhotoUpload,
  localProfile 
}) {
  return (
    <section className="flex flex-col items-center gap-3 pb-6 border-b border-[var(--border-color)]">
      <div className="relative">
        <div className="w-24 h-24 rounded-full border-4 border-[var(--bg-card)] shadow-md overflow-hidden bg-[var(--bg-chip)]">
          <img 
            src={avatarUrl} 
            alt="Avatar" 
            className="w-full h-full object-cover"
            onError={(e) => { 
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full items-center justify-center bg-[var(--bg-header)] text-[var(--accent-sky)]" style={{display: 'none'}}>
            <User size={40} />
          </div>
        </div>
        <button
          data-testid="btn-change-photo"
          onClick={() => photoInputRef.current?.click()}
          disabled={isUploadingPhoto || !backendAvailable}
          className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[var(--accent-primary)] text-white flex items-center justify-center shadow-lg hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
        >
          {isUploadingPhoto ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Camera size={16} />
          )}
        </button>
        <input
          ref={photoInputRef}
          data-testid="input-photo-upload"
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={onPhotoUpload}
          className="hidden"
        />
      </div>
      <div className="text-center">
        <h2 className="text-lg font-bold text-[var(--text-primary)]">
          {localProfile.name || 'Seu Nome'}
        </h2>
        <p className="text-xs text-[var(--text-secondary)]">Vendedor · MA</p>
        {!backendAvailable && (
          <p className="text-xs text-amber-500 mt-1">Offline - foto será salva localmente</p>
        )}
      </div>
    </section>
  );
}

// Profile Form Fields
export function ProfileFields({ localProfile, onProfileChange, onPhoneChange }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-[var(--text-primary)]">Perfil</h3>
      
      <div className="flex items-center gap-3">
        <User size={18} className="text-[var(--text-muted)]" />
        <input
          data-testid="input-profile-name"
          type="text"
          value={localProfile.name}
          onChange={(e) => onProfileChange('name', e.target.value)}
          placeholder="Seu nome"
          className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
        />
      </div>

      <div className="flex items-center gap-3">
        <Building2 size={18} className="text-[var(--text-muted)]" />
        <input
          data-testid="input-profile-company"
          type="text"
          value={localProfile.company}
          onChange={(e) => onProfileChange('company', e.target.value)}
          placeholder="Empresa"
          className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
        />
      </div>

      <div className="flex items-center gap-3">
        <Phone size={18} className="text-[var(--text-muted)]" />
        <input
          data-testid="input-profile-phone"
          type="tel"
          value={localProfile.phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="(xxx) xxx-xxxx"
          className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
        />
      </div>

      <div className="flex items-center gap-3">
        <Mail size={18} className="text-[var(--text-muted)]" />
        <input
          data-testid="input-profile-email"
          type="email"
          value={localProfile.email}
          onChange={(e) => onProfileChange('email', e.target.value)}
          placeholder="Email"
          className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
        />
      </div>
    </section>
  );
}

// Download helper function
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Export functions
export function exportToCSV(lojas) {
  const headers = ['Nome', 'Endereço', 'Cidade', 'Estado', 'ZIP', 'Gerente', 'Vendedores', 'Telefones', 'Observações', 'Última Visita'];
  const rows = lojas.map(loja => [
    loja.name,
    loja.address,
    loja.city,
    loja.state,
    loja.zip,
    loja.manager || '',
    (loja.sellers || []).map(s => s.name).join('; '),
    (loja.sellers || []).map(s => s.phone).join('; '),
    loja.notes || '',
    loja.visit || ''
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, 'dealer-manager.csv');
}

export function exportToJSON(lojas) {
  const data = JSON.stringify(lojas, null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  downloadBlob(blob, 'dealer-backup.json');
}
